import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { userDirectoryApi } from '../api/user-directory-api';
import type { UserBasicInfo } from './types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BATCH_SIZE = 50;

export const userDirectoryKeys = {
  all: ['user-directory'] as const,
  basic: (id: string) => [...userDirectoryKeys.all, 'basic', id] as const,
  batch: (ids: readonly string[]) =>
    [...userDirectoryKeys.all, 'batch', [...ids].sort().join(',')] as const,
};

export function useBasicUserQuery(id: string | null | undefined) {
  return useQuery({
    queryKey: userDirectoryKeys.basic(id ?? ''),
    queryFn: () => userDirectoryApi.basic(id!),
    enabled: Boolean(id) && (id ? UUID_RE.test(id) : false),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/**
 * Batch-resolve UUIDs into display names. Splits into chunks of {@link BATCH_SIZE}
 * to stay under the backend cap and dedupes input.
 */
export function useBasicUsers(ids: ReadonlyArray<string | null | undefined>): Map<string, UserBasicInfo> {
  const uniqueIds = useMemo(() => {
    const set = new Set<string>();
    for (const id of ids) {
      if (id && UUID_RE.test(id)) set.add(id);
    }
    return [...set];
  }, [ids]);

  const chunks = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      out.push(uniqueIds.slice(i, i + BATCH_SIZE));
    }
    return out;
  }, [uniqueIds]);

  const results = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: userDirectoryKeys.batch(chunk),
      queryFn: () => userDirectoryApi.basicBatch(chunk),
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
    })),
  });

  return useMemo(() => {
    const map = new Map<string, UserBasicInfo>();
    for (const r of results) {
      if (r.data) {
        for (const u of r.data) map.set(u.id, u);
      }
    }
    return map;
  }, [results]);
}
